import Button from "../form/button";
import toast from "react-hot-toast";
import InputField from "../form/input";
import { RootState } from "../../app/store";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useState, useRef } from "react";
import { setUser } from "../../features/users/usersSlice";
import { CREATE_ORDER } from "../../api/orders/mutations";
import {
  isOTPValid,
  isValidAddressString,
  phoneValidity,
} from "../../utils/validation";
import { getSessionId } from "../../utils/audio-analytics";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { formatOTP, formatPhone, removeSpace } from "../../utils/format";
import { CREATE_PAYMENT, PAYMENT } from "../../api/payments/mutations";
import { toggleUpSalesModal } from "../../features/products/productsSlice";
import useFacebookPixel from "../../hooks/useFacebookPixel";
import { PRICING_ZONES } from "../../api/utils/queries";

// Validation functions for firstname and lastname
const validateName = (name: string): boolean => {
  if (!name || name.trim().length === 0) {
    return false;
  }

  // Check minimum length (at least 2 characters)
  if (name.trim().length < 2) {
    return false;
  }

  // Check maximum length (reasonable limit)
  if (name.trim().length > 50) {
    return false;
  }

  // Check for valid characters (letters, spaces, hyphens, apostrophes)
  const validNameRegex = /^[a-zA-ZÀ-ÿ\s\-']+$/;
  if (!validNameRegex.test(name.trim())) {
    return false;
  }

  return true;
};

interface Props {
  handleDismiss: any;
  item?: any;
  pixelId?: string;
}

const Validation = (props: Props) => {
  const navigate = useNavigate();
  const user = useAppSelector((state: RootState) => state.users.user);
  const location = useAppSelector((state: RootState) => state.utils.location);
  const [state, setState] = useState({
    firstname: "",
    lastname: "",
    phone:
      user?.phone === null
        ? ""
        : formatPhone(location?.country_code2, user?.phone),
    isPhoneValidated: false,
    isAddressValidated: true,
    isCodeValid: true,
    isOTPSent: false,
    code: "",
    isWaiting: false,
    isMobile: true,
    address: "",
    name: "",
    isNameValid: true,
  });
  const [addressSuggestions, setAddressSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allAddresses, setAllAddresses] = useState<string[]>([]);
  const addressInputRef = useRef<HTMLDivElement>(null);
  
  const calling_code = useAppSelector(
    (state: RootState) => state.utils.location?.calling_code ?? ""
  );

  // Récupérer les zones de tarification
  useQuery(PRICING_ZONES, {
    onCompleted: (data) => {
      if (data?.pricingZones) {
        // Extraire tous les quartiers de toutes les zones
        const addresses: string[] = [];
        data.pricingZones.forEach((zone: any) => {
          if (zone.neighborhoods && Array.isArray(zone.neighborhoods)) {
            addresses.push(...zone.neighborhoods);
          }
        });
        setAllAddresses(addresses);
      }
    },
  });

  const { trackEvent, trackCustomEvent } = useFacebookPixel(props.pixelId);

  const paymentMode = useAppSelector(
    (state: RootState) => state.payments.payment?.paymentMode ?? "CASH"
  );
  const total = useAppSelector((state: RootState) => state.cart.total);
  const cartIsDeliveryFree = useAppSelector(
    (state: RootState) => state.cart.isDeliveryFree
  );
  const isDeliveryFree = props?.item
    ? props.item.isDeliveryFree !== false
    : (cartIsDeliveryFree || true);
  const deliveryPrice = isDeliveryFree ? 0 : 1500;
  let cartItems = useAppSelector((state: RootState) => state.cart.cartItems);
  if (props.item) {
    cartItems = [props.item];
  }
  const [payment] = useMutation(PAYMENT, {
    onCompleted: (data) => {
      if (data) {
        const { success } = data?.payment;
        switch (paymentMode) {
          case "OM":
            if (success) {
              toast.success(
                "Veuillez vous assurer que votre solde est suffisant."
              );
            } else {
              toast.error("Erreur lors du paiement veuillez réessayer.");
            }
            break;
          case "WAVE":
            if (success) {
              const url = data?.payment?.url;
              toast.success(
                "Veuillez valider votre paiement avec votre application wave."
              );
              if (url) window.open(url, "_blank");
            } else {
              toast.error("Erreur lors du paiement veuillez réessayer.");
            }
            break;
          default:
            break;
        }
      }
    },
    onError: (error) => {
      setState((prevState) => ({
        ...prevState,
        isWaiting: false,
      }));
      console.error(error);
    },
  });

  const [createPayment] = useMutation(CREATE_PAYMENT, {
    onCompleted: (data) => {
      if (data) {
        setState((prevState) => ({
          ...prevState,
          isWaiting: false,
        }));
        const payToken = data?.createPayment?.token;
        switch (paymentMode) {
          case "OM":
            if (payToken) {
              payment({
                variables: {
                  paymentInput: {
                    token: payToken,
                    phone: removeSpace(state.phone),
                    code: removeSpace(state.code),
                    calling_code,
                    paymentMode: "OM",
                  },
                },
              });
            }
            dispatch(props.handleDismiss());
            toast.success("Votre commande à été prise en compte");
            toast.success("Veuillez attendre l'appel du livreur");
            break;
          case "WAVE":
            if (payToken) {
              payment({
                variables: {
                  paymentInput: {
                    token: payToken,
                    phone: removeSpace(state.phone),
                    calling_code,
                    paymentMode: "WAVE",
                  },
                },
              });
            }
            dispatch(props.handleDismiss());
            toast.success("Votre commande à été prise en compte");
            toast.success("Veuillez attendre l'appel du livreur");
            break;
          default:
            break;
        }
        navigate("/orders");
      }
    },
    onError: (error) => {
      setState((prevState) => ({
        ...prevState,
        isWaiting: false,
      }));
      console.error(error);
    },
  });

  const [submitOrder] = useMutation(CREATE_ORDER, {
    onCompleted: (data) => {
      if (data) {
        setState((prevState) => ({
          ...prevState,
          isWaiting: false,
        }));
        trackEvent("Purchase", {
          currency: "XOF",
          value: data?.createOrder?.amount,
          content_ids: [
            ...(cartItems?.map((item) => item?._id).filter(Boolean) ?? []),
          ],
          content_type: "product",
          userData: {
            firstName: state.firstname,
            lastName: state.lastname,
            phone: removeSpace(state.phone)
              ? `${calling_code}${removeSpace(state.phone)}`
              : undefined,
            country: location?.country_code2,
          },
        });
        switch (paymentMode) {
          case "CASH":
            toast.success("Votre commande à été prise en compte", {
              duration: 7000,
            });
            toast.success(
              "Veuillez attendre l'appel de l'opératrice de commande",
              { duration: 7000 }
            );
            dispatch(props.handleDismiss());
            dispatch(toggleUpSalesModal());
            break;
          case "OM":
            toast.success("Votre commande à été prise en compte", {
              duration: 7000,
            });
            toast.success(
              "Veuillez attendre l'appel de l'opératrice de commande",
              { duration: 7000 }
            );
            dispatch(props.handleDismiss());
            dispatch(toggleUpSalesModal());
            createPayment({
              variables: {
                paymentMode: "OM",
                orderId: data?.createOrder?._id,
                amount: data?.createOrder?.amount,
                phone: removeSpace(state.phone),
                calling_code,
              },
            });
            break;
          case "WAVE":
            toast.success("Votre commande à été prise en compte", {
              duration: 7000,
            });
            toast.success(
              "Veuillez attendre l'appel de l'opératrice de commande",
              { duration: 7000 }
            );
            dispatch(props.handleDismiss());
            dispatch(toggleUpSalesModal());
            createPayment({
              variables: {
                paymentMode: "WAVE",
                orderId: data?.createOrder?._id,
                amount: data?.createOrder?.amount,
                phone: removeSpace(state.phone),
                calling_code,
              },
            });
            break;
          default:
            break;
        }
      }
    },
    onError: (error) => {
      setState((prevState) => ({
        ...prevState,
        isWaiting: false,
      }));
      console.error(error);
    },
  });

  const dispatch = useAppDispatch();

  // Focus sur le premier champ au montage du composant
  useEffect(() => {
    const phoneInput = document.getElementById('phone');
    if (phoneInput) {
      // Petit délai pour s'assurer que le modal est complètement rendu
      setTimeout(() => {
        phoneInput.focus();
      }, 100);
    }
  }, []);

  // Fermer les suggestions quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addressInputRef.current && !addressInputRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fonction pour filtrer et trier les adresses par pertinence
  const filterAddresses = (input: string) => {
    if (!input || input.length < 2) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const searchTerm = input.toLowerCase().trim();

    // Filtrer et calculer le score de pertinence pour chaque adresse
    const scoredAddresses = allAddresses
      .filter(
        (address) =>
          typeof address === "string" &&
          address.toLowerCase().includes(searchTerm)
      )
      .map((address) => {
        const addressLower = address.toLowerCase();
        let score = 0;

        // Score 1000: Correspondance exacte (insensible à la casse)
        if (addressLower === searchTerm) {
          score = 1000;
        }
        // Score 900: Commence exactement par le terme de recherche
        else if (addressLower.startsWith(searchTerm)) {
          score = 900;
        }
        // Score 800: Commence par le terme après un espace (début de mot)
        else if (addressLower.includes(' ' + searchTerm)) {
          score = 800;
        }
        // Score 700: Commence par le terme après un tiret ou parenthèse
        else if (addressLower.includes('-' + searchTerm) || addressLower.includes('(' + searchTerm)) {
          score = 700;
        }
        // Score 500: Contient le terme au milieu
        else {
          score = 500;
        }

        // Bonus: Plus l'adresse est courte, plus elle est pertinente (en cas d'égalité)
        score -= address.length * 0.1;

        return { address, score };
      });

    // Trier par score décroissant
    const sortedAddresses = scoredAddresses
      .sort((a, b) => b.score - a.score)
      .map((item) => item.address)
      .slice(0, 10); // Limiter à 10 suggestions

    setAddressSuggestions(sortedAddresses);
    setShowSuggestions(sortedAddresses.length > 0);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const name = e?.target?.name;
    const raw = e?.target?.value ?? "";
    if (!name) return;

    switch (name) {
      case "phone":
        setState((prevState) => ({
          ...prevState,
          isPhoneValidated: phoneValidity(location?.country_code2, raw),
          [name]: formatPhone(location?.country_code2, raw),
        }));
        dispatch(setUser({ phone: removeSpace(raw) }));
        break;
      case "code":
        setState((prevState) => ({
          ...prevState,
          isCodeValid: isOTPValid(raw),
          [name]: formatOTP(raw),
        }));
        break;
      case "address":
        setState((prevState) => ({
          ...prevState,
          isAddressValidated: isValidAddressString(raw),
          [name]: raw,
        }));
        filterAddresses(raw);
        break;
      case "name": {
        const parts = raw.split(" ");
        setState((prevState) => ({
          ...prevState,
          [name]: raw,
          isNameValid: validateName(raw),
          firstname: parts[0] || "",
          lastname: parts.slice(1).join(" ") || "",
        }));
        break;
      }
      default:
        setState((prevState) => ({
          ...prevState,
          [name]: raw,
        }));
        break;
    }
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    setState((prevState) => ({
      ...prevState,
      isWaiting: true,
    }));
    const amount = props?.item?.selling_for ?? total + deliveryPrice;

    submitOrder({
      variables: {
        firstname: state.firstname,
        lastname: state.lastname,
        phone: removeSpace(state.phone),
        address: state.address,
        calling_code,
        country_flag: location?.country_flag,
        amount,
        isDeliveryFree,
        sessionId: getSessionId(),
        products: cartItems
          ?.map((item) => {
            const pid = item?._id;
            const shopId = item?.shop?._id;
            if (!pid || !shopId) return null;
            return {
              product: pid,
              quantity: item?.quantity ?? 1,
              shop: shopId,
            };
          })
          .filter(Boolean),
      },
    });
  };

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
    }));
  }, []);

  return (
    <>
      <div className="bg-gray-50 flex justify-center items-center flex-col py-7">
        <div className="flex justify-center items-center bg-white w-full h-12 ">
          <span className="font-bold text-2xl">
            {
              {
                CASH: "Informations de livraison",
                OM: "Payé par Orange Money",
                WAVE: "Payé par Wave",
              }[paymentMode] ?? "Informations de livraison"
            }
          </span>
        </div>
        <div className="flex justify-center p-3 md:p-7 pb-0 flex-col w-full gap-1">
          <InputField
            id="phone"
            type="tel"
            autoFocus={true}
            labelName="phone"
            value={state.phone}
            icon={location?.country_flag}
            calling_code={location?.calling_code}
            placeholder="77 xxx xx xx"
            handleInputChange={handleInputChange}
            autoComplete="off"
            isValid={
              state.phone.length === 0
                ? true
                : state.isPhoneValidated
                ? true
                : false
            }
            info={location?.country_code2?.toLocaleUpperCase?.() ?? ""}
          />
          <InputField
            type="text"
            id="name"
            isRequired={true}
            labelName="name"
            value={state.name}
            isValid={state.isNameValid}
            placeholder="Entrez le nom & prénom"
            handleInputChange={handleInputChange}
            autoComplete="off"
          />
          <div className="relative" ref={addressInputRef}>
            <InputField
              id="address"
              type="address"
              labelName="address"
              value={state.address}
              placeholder="Entrez votre adresse ici"
              handleInputChange={handleInputChange}
              autoComplete="off"
              multiline
              isValid={
                state.address.length === 0
                  ? true
                  : state.isAddressValidated
                  ? true
                  : false
              }
              info={location?.country_code2?.toLocaleUpperCase?.() ?? ""}
              styles="w-full"
            />
            {showSuggestions && addressSuggestions.length > 0 && (
              <div className="absolute z-50 w-full bg-white shadow-lg rounded-md mt-1 max-h-60 overflow-y-auto border border-gray-200">
                {addressSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer transition-colors duration-150"
                    onClick={() => {
                      setState((prevState) => ({
                        ...prevState,
                        address: suggestion,
                        isAddressValidated: true,
                      }));
                      setShowSuggestions(false);
                    }}
                  >
                    {suggestion}
                  </div>
                ))}
              </div>
            )}
          </div>
          {paymentMode === "OM" && state.isPhoneValidated && (
            <div className="flex justify-center items-center bg-white rounded-md flex-col p-2">
              <span className="text-center">
                Tapez{" "}
                <span className="text-gray-500 text-sm">
                  <span className="text-green-600">#</span>144
                  <span className="text-yellow-500">#</span>391
                  <span className="text-green-600">*</span>VOTRE_CODE_OM
                  <span className="text-red-600">#</span>
                </span>{" "}
                pour générer le code de la transaction{" "}
              </span>
            </div>
          )}
          <InputField
            id="code"
            type="text"
            hidden={!state.isPhoneValidated || !(paymentMode === "OM")}
            value={state.code}
            isValid={state.isCodeValid ? true : false}
            labelName="code"
            placeholder="Tapez le code reçu par sms"
            handleInputChange={handleInputChange}
            autoComplete="off"
            styles={"w-full"}
          />
        </div>
        <div className="w-1/2">
          <Button
            title="Valider"
            isWaiting={state.isWaiting}
            handleOnClick={handleSubmit}
            disabled={
              !state.isPhoneValidated ||
              state.isWaiting ||
              (paymentMode === "OM" && state.code.length === 0
                ? true
                : !state.isCodeValid
                ? true
                : false)
            }
          />
        </div>
      </div>
    </>
  );
};

export default Validation;
